import React, { useState } from 'react';
import { X, Play, Pause, ShoppingCart, Download, Sparkles, Info } from 'lucide-react';
import BeatPurchaseModal from './BeatPurchaseModal';

const BeatCatalog = ({ isOpen, onClose }) => {
  const [playingBeat, setPlayingBeat] = useState(null);
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [purchaseModal, setPurchaseModal] = useState({ isOpen: false, beat: null, licenseType: null });
  const [showInfo, setShowInfo] = useState(null);

  const licenseInfo = {
    lease: {
      title: 'Lease License - $40',
      features: [
        '✓ Create 1 commercial song',
        '✓ 1-year term',
        '✓ Distribute worldwide (Spotify, Apple Music, etc.)',
        '✓ 1 music video allowed',
        '✓ Live performances',
        '✗ Beat can be sold to others',
        '✗ No ownership rights',
        '⚠️ All royalties remain with producer'
      ]
    },
    unlimited: {
      title: 'Unlimited License - $80',
      features: [
        '✓ Unlimited commercial songs',
        '✓ No expiration',
        '✓ Unlimited distribution & streams',
        '✓ Unlimited music videos',
        '✓ Radio & promotional use',
        '✗ Beat can be sold to others',
        '✗ No ownership rights',
        '⚠️ All royalties remain with producer'
      ]
    },
    exclusive: {
      title: 'Exclusive Rights - Custom Price',
      features: [
        '✓ Full exclusive ownership',
        '✓ Beat removed from sale',
        '✓ Stems/Trackouts included',
        '✓ Unlimited songs & videos',
        '✓ Film/TV/Commercial use',
        '✓ Worldwide distribution',
        '✓ 50/50 royalty split with producer',
        '⚠️ Requires approval (24-48 hours)'
      ]
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      fetchBeats();
    }
  }, [isOpen]);

  const fetchBeats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/beats?is_active=true`);
      const data = await response.json();
      setBeats(data.map(beat => ({
        id: beat.id,
        title: beat.title,
        producer_name: beat.producer_name || 'Trap Spade',
        bpm: beat.bpm,
        key: beat.key,
        price: beat.lease_price || 40,
        unlimited: beat.unlimited_price || 80,
        exclusive: beat.exclusive_price,
        audio_url: beat.audio_url
      })));
    } catch (error) {
      console.error('Error fetching beats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = (beat, licenseType) => {
    setPurchaseModal({ isOpen: true, beat, licenseType });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-gradient-to-br from-purple-900/40 to-blue-900/40 backdrop-blur-xl border border-purple-500/30 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-purple-500/30">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">Beat Catalog</h2>
              <p className="text-gray-400 text-sm">Browse and purchase exclusive beats</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-purple-900/30 hover:bg-purple-900/50 flex items-center justify-center transition-colors"
            >
              <X className="text-white" size={20} />
            </button>
          </div>
          <p className="text-xs text-purple-300/70 italic mt-2">
            Click any purchase option below to view complete beat details and licensing information
          </p>
        </div>

        {/* Beat List */}
        <div className="p-4 md:p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-400">Loading beats...</p>
            </div>
          ) : beats.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">No beats available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid gap-4">
            {beats.map((beat) => (
              <div
                key={beat.id}
                className="bg-black/40 border border-purple-500/20 rounded-xl p-4 hover:border-purple-500/50 transition-all"
              >
                {/* Mobile Layout */}
                <div className="flex flex-col space-y-4 md:hidden">
                  <div className="flex items-center space-x-4">
                    {/* Play Button */}
                    <button
                      onClick={() => setPlayingBeat(playingBeat === beat.id ? null : beat.id)}
                      className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center hover:from-purple-700 hover:to-blue-700 transition-all flex-shrink-0"
                    >
                      {playingBeat === beat.id ? (
                        <Pause size={20} className="text-white" />
                      ) : (
                        <Play size={20} className="text-white ml-0.5" />
                      )}
                    </button>

                    {/* Beat Info */}
                    <div className="flex-1">
                      <h3 className="text-white font-semibold text-base mb-1">{beat.title}</h3>
                      <p className="text-purple-300 text-xs mb-1">by {beat.producer_name}</p>
                      <div className="flex items-center space-x-2 text-xs text-gray-400">
                        <span>{beat.bpm} BPM</span>
                        <span>•</span>
                        <span>Key: {beat.key}</span>
                      </div>
                    </div>
                  </div>

                  {/* Pricing & Actions Mobile */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-purple-900/20 border border-purple-500/20 rounded-lg p-2 relative">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs text-gray-400">Lease</div>
                        <button
                          onClick={() => setShowInfo(showInfo === 'lease' ? null : 'lease')}
                          className="text-purple-400 hover:text-purple-300"
                        >
                          <Info size={14} />
                        </button>
                      </div>
                      <div className="text-purple-400 font-bold mb-2 text-sm">${beat.price}</div>
                      <button 
                        onClick={() => handlePurchase(beat, 'lease')}
                        className="w-full px-2 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-xs"
                      >
                        Buy
                      </button>
                    </div>
                    <div className="bg-blue-900/20 border border-blue-500/20 rounded-lg p-2 relative">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs text-gray-400">Unlimited</div>
                        <button
                          onClick={() => setShowInfo(showInfo === 'unlimited' ? null : 'unlimited')}
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Info size={14} />
                        </button>
                      </div>
                      <div className="text-blue-400 font-bold mb-2 text-sm">${beat.unlimited}</div>
                      <button 
                        onClick={() => handlePurchase(beat, 'unlimited')}
                        className="w-full px-2 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
                      >
                        Buy
                      </button>
                    </div>
                    <div className="bg-green-900/20 border border-green-500/20 rounded-lg p-2 relative">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs text-gray-400">Exclusive</div>
                        <button
                          onClick={() => setShowInfo(showInfo === 'exclusive' ? null : 'exclusive')}
                          className="text-green-400 hover:text-green-300"
                        >
                          <Info size={14} />
                        </button>
                      </div>
                      <div className="text-green-400 font-bold mb-2 text-sm">${beat.exclusive}</div>
                      <button 
                        onClick={() => handlePurchase(beat, 'exclusive')}
                        className="w-full px-2 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs"
                      >
                        Buy
                      </button>
                    </div>
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Play Button */}
                    <button
                      onClick={() => setPlayingBeat(playingBeat === beat.id ? null : beat.id)}
                      className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center hover:from-purple-700 hover:to-blue-700 transition-all"
                    >
                      {playingBeat === beat.id ? (
                        <Pause size={20} className="text-white" />
                      ) : (
                        <Play size={20} className="text-white ml-0.5" />
                      )}
                    </button>

                    {/* Beat Info */}
                    <div>
                      <h3 className="text-white font-semibold text-lg mb-1">{beat.title}</h3>
                      <p className="text-purple-300 text-sm mb-1">by {beat.producer_name}</p>
                      <div className="flex items-center space-x-3 text-sm text-gray-400">
                        <span>{beat.bpm} BPM</span>
                        <span>•</span>
                        <span>Key: {beat.key}</span>
                      </div>
                    </div>
                  </div>

                  {/* Pricing & Actions Desktop */}
                  <div className="flex items-center space-x-4">
                    {/* Lease */}
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <div className="text-xs text-gray-400 flex items-center space-x-1">
                          <span>Lease</span>
                          <button
                            onClick={() => setShowInfo(showInfo === 'lease' ? null : 'lease')}
                            className="text-purple-400 hover:text-purple-300"
                          >
                            <Info size={14} />
                          </button>
                        </div>
                        <div className="text-purple-400 font-bold">${beat.price}</div>
                      </div>
                      <button 
                        onClick={() => handlePurchase(beat, 'lease')}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
                      >
                        <ShoppingCart size={16} />
                        <span>Lease</span>
                      </button>
                    </div>

                    {/* Unlimited */}
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <div className="text-xs text-gray-400 flex items-center space-x-1">
                          <span>Unlimited</span>
                          <button
                            onClick={() => setShowInfo(showInfo === 'unlimited' ? null : 'unlimited')}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Info size={14} />
                          </button>
                        </div>
                        <div className="text-blue-400 font-bold">${beat.unlimited}</div>
                      </div>
                      <button 
                        onClick={() => handlePurchase(beat, 'unlimited')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                      >
                        <Sparkles size={16} />
                        <span>Unlimited</span>
                      </button>
                    </div>

                    {/* Exclusive */}
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <div className="text-xs text-gray-400 flex items-center space-x-1">
                          <span>Exclusive</span>
                          <button
                            onClick={() => setShowInfo(showInfo === 'exclusive' ? null : 'exclusive')}
                            className="text-green-400 hover:text-green-300"
                          >
                            <Info size={14} />
                          </button>
                        </div>
                        <div className="text-green-400 font-bold">${beat.exclusive}</div>
                      </div>
                      <button 
                        onClick={() => handlePurchase(beat, 'exclusive')}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                      >
                        <Download size={16} />
                        <span>Exclusive</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            </div>
          )}

          {/* Coming Soon Message */}
          <div className="mt-6 text-center p-6 bg-purple-900/20 border border-purple-500/20 rounded-xl">
            <p className="text-gray-400">
              More beats coming soon! Contact us for custom beat production.
            </p>
            <a
              href="mailto:info@hqstu.com"
              className="inline-block mt-3 px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all"
            >
              Request Custom Beat
            </a>
          </div>
        </div>
      </div>

      {/* Purchase Modal */}
      <BeatPurchaseModal 
        isOpen={purchaseModal.isOpen}
        onClose={() => setPurchaseModal({ isOpen: false, beat: null, licenseType: null })}
        beat={purchaseModal.beat}
        licenseType={purchaseModal.licenseType}
      />

      {/* License Info Modal */}
      {showInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setShowInfo(null)}>
          <div className="relative w-full max-w-md bg-gradient-to-br from-purple-900/40 to-blue-900/40 backdrop-blur-xl border border-purple-500/30 rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-purple-500/30">
              <h3 className="text-xl font-bold text-white">{licenseInfo[showInfo].title}</h3>
              <button
                onClick={() => setShowInfo(null)}
                className="w-8 h-8 rounded-full bg-purple-900/30 hover:bg-purple-900/50 flex items-center justify-center transition-colors"
              >
                <X className="text-white" size={18} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-2">
                {licenseInfo[showInfo].features.map((feature, index) => (
                  <div
                    key={index}
                    className={`text-sm py-2 px-3 rounded-lg ${
                      feature.startsWith('✓')
                        ? 'bg-green-900/20 text-green-300'
                        : feature.startsWith('✗')
                        ? 'bg-red-900/20 text-red-300'
                        : 'bg-yellow-900/20 text-yellow-300'
                    }`}
                  >
                    {feature}
                  </div>
                ))}
              </div>

              <button
                onClick={() => setShowInfo(null)}
                className="mt-6 w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BeatCatalog;
